import { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import { formatPrice } from '../../../utils/format';

export default function VentesTab() {
  const [ventesTissu, setVentesTissu] = useState<any[]>([]);
  const [commandes, setCommandes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const [vtArr, cmdArr] = await Promise.all([
          api.getTodayVentes(token),
          api.getCommandes(token)
        ]);
        setVentesTissu(Array.isArray(vtArr) ? vtArr : []);
        setCommandes(Array.isArray(cmdArr) ? cmdArr : []);
      } catch (err) {
        console.error("Erreur chargement ventes:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // On considère les ventes du jour comme "ventes tissu" + "acomptes commandes aujourd'hui"
  const todayStr = new Date().toISOString().split('T')[0];
  const acomptesAujourdhui = commandes.filter(c => c.date_commande === todayStr);
  
  const totalTissu = ventesTissu.reduce((s, v) => s + Number(v.total), 0);
  const totalAcomptes = acomptesAujourdhui.reduce((s, c) => s + Number(c.acompte || 0), 0);
  const grandTotal = totalTissu + totalAcomptes;

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foret-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Résumé financier du jour */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-foret-600 text-white rounded-2xl p-6 shadow-xl shadow-foret-100">
          <p className="text-foret-100 text-xs font-bold uppercase tracking-widest mb-1">Total Encaissé (Jour)</p>
          <h2 className="text-3xl font-black">{formatPrice(grandTotal)}</h2>
          <div className="mt-4 flex items-center gap-2 text-xs text-foret-200">
            <i className="ri-information-line" />
            <span>Tissus + Acomptes commandes</span>
          </div>
        </div>
        
        <div className="bg-white border border-gray-100 rounded-2xl p-6">
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Ventes Tissu</p>
          <h2 className="text-3xl font-bold text-gray-900">{formatPrice(totalTissu)}</h2>
          <p className="text-xs text-gray-500 mt-2">{ventesTissu.length} transaction(s)</p>
        </div>

        <div className="bg-white border border-gray-100 rounded-2xl p-6">
          <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-1">Acomptes Commandes</p>
          <h2 className="text-3xl font-bold text-gray-900">{formatPrice(totalAcomptes)}</h2>
          <p className="text-xs text-gray-500 mt-2">{acomptesAujourdhui.length} nouvelle(s) commande(s)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Dernières ventes de tissu */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-playfair font-bold text-gray-900">Ventes tissus du jour</h3>
            <i className="ri-shopping-bag-3-line text-gray-300" />
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {ventesTissu.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucune vente de tissu aujourd&apos;hui</div>
            ) : ventesTissu.map(v => (
              <div key={v.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-foret-600">
                    <i className="ri-scissors-cut-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">{v.tissu_nom}</p>
                    <p className="text-xs text-gray-500">{v.quantite} {v.tissu_unite || 'm'} · {v.client_nom || 'Client anonyme'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-gray-900">{formatPrice(v.total, false)}</p>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">{v.mode_paiement}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Derniers acomptes (commandes) */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-playfair font-bold text-gray-900">Nouveaux acomptes</h3>
            <i className="ri-hand-coin-line text-gray-300" />
          </div>
          <div className="divide-y divide-gray-50 max-h-[400px] overflow-y-auto">
            {acomptesAujourdhui.length === 0 ? (
              <div className="text-center py-10 text-gray-400 text-sm">Aucun acompte encaissé aujourd&apos;hui</div>
            ) : acomptesAujourdhui.map(c => (
              <div key={c.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-terre-600">
                    <i className="ri-folder-add-line text-lg" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">CMD #{c.code}</p>
                    <p className="text-xs text-gray-500">{c.client_nom}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-foret-700">{formatPrice(c.acompte || 0, false)}</p>
                  <p className="text-[10px] text-gray-400 uppercase font-bold">Acompte</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
